<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  sharedOpenId,
  sharedPinnedId,
  closeAppsDropdown,
} from './appsDropdownState';

const props = withDefaults(
  defineProps<{
    label: string;
    closeDelay?: number;
  }>(),
  {
    closeDelay: 150,
  },
);

const myId = Symbol('itp-dropdown-submenu');

const isOpen = computed(() => sharedOpenId.value === myId);
const pinned = computed(() => sharedPinnedId.value === myId);

const triggerEl = ref<HTMLButtonElement | null>(null);
const flyoutTop = ref(0);
const flyoutLeft = ref(0);
const flyoutMaxHeight = ref(0);
const VIEWPORT_PADDING = 16;
const MIN_FLYOUT_HEIGHT = 240;

function measure() {
  const trigger = triggerEl.value;
  if (!trigger) {
    return;
  }
  const menu = trigger.closest(
    '.itp-apps-dropdown__menu',
  ) as HTMLElement | null;
  const tRect = trigger.getBoundingClientRect();
  const mRect = menu?.getBoundingClientRect();

  const viewportHeight = window.innerHeight;

  let top = tRect.top;
  let availableHeight = viewportHeight - top - VIEWPORT_PADDING;

  if (availableHeight < MIN_FLYOUT_HEIGHT) {
    top = Math.max(
      VIEWPORT_PADDING,
      viewportHeight - MIN_FLYOUT_HEIGHT - VIEWPORT_PADDING,
    );
    availableHeight = viewportHeight - top - VIEWPORT_PADDING;
  }

  flyoutTop.value = top;
  flyoutLeft.value = mRect ? mRect.right : tRect.right;
  flyoutMaxHeight.value = availableHeight;
}

let closeTimer: number | null = null;
function cancelClose() {
  if (closeTimer !== null) {
    window.clearTimeout(closeTimer);
    closeTimer = null;
  }
}
function scheduleClose() {
  if (pinned.value) {
    return;
  }
  cancelClose();
  closeTimer = window.setTimeout(() => {
    if (sharedOpenId.value === myId) {
      sharedOpenId.value = null;
    }
  }, props.closeDelay);
}

function doOpen() {
  cancelClose();
  measure();
  sharedOpenId.value = myId;
}

function doClose() {
  cancelClose();
  if (sharedOpenId.value === myId) {
    sharedOpenId.value = null;
  }
}

function onTriggerEnter() {
  if (sharedPinnedId.value !== null && sharedPinnedId.value !== myId) {
    return;
  }
  doOpen();
}
function onTriggerLeave() {
  scheduleClose();
}
function onFlyoutEnter() {
  cancelClose();
}
function onFlyoutLeave() {
  scheduleClose();
}

function onTriggerClick(e: MouseEvent) {
  e.stopPropagation();
  e.preventDefault();
  if (pinned.value) {
    sharedPinnedId.value = null;
    doClose();
  } else {
    sharedPinnedId.value = myId;
    doOpen();
  }
}

function onFlyoutClick() {
  window.setTimeout(() => {
    if (sharedPinnedId.value === myId) {
      sharedPinnedId.value = null;
    }
    doClose();
    closeAppsDropdown();
  }, 0);
}
function onWindowChange() {
  if (isOpen.value) {
    measure();
  }
}

onMounted(() => {
  window.addEventListener('resize', onWindowChange);
  window.addEventListener('scroll', onWindowChange, true);
});

onBeforeUnmount(() => {
  cancelClose();
  window.removeEventListener('resize', onWindowChange);
  window.removeEventListener('scroll', onWindowChange, true);
  if (sharedOpenId.value === myId) {
    sharedOpenId.value = null;
  }
  if (sharedPinnedId.value === myId) {
    sharedPinnedId.value = null;
  }
});

const flyoutStyle = computed<Record<string, string>>(() => ({
  position: 'fixed',
  top: `${flyoutTop.value}px`,
  left: `${flyoutLeft.value}px`,
  margin: '0',
  minWidth: '240px',
  maxWidth: 'none',
  maxHeight: `${flyoutMaxHeight.value}px`,
  overflowY: 'auto',
  overflowX: 'hidden',
  zIndex: '1080',
  background: 'var(--bs-body-bg, #fff)',
  border: '1px solid var(--bs-border-color, #dee2e6)',
  borderRadius: '0.375rem',
  boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
  padding: '0.5rem 0',
  listStyle: 'none',
}));
</script>

<template>
  <li
    class="itp-dropdown-submenu"
    :class="{ 'is-active': isOpen, 'is-pinned': pinned }"
  >
    <button
      ref="triggerEl"
      type="button"
      class="dropdown-item itp-dropdown-submenu__trigger"
      :aria-haspopup="true"
      :aria-expanded="isOpen"
      @click="onTriggerClick"
      @pointerenter="onTriggerEnter"
      @pointerleave="onTriggerLeave"
    >
      <span class="itp-dropdown-submenu__label">{{ label }}</span>
      <svg
        class="itp-dropdown-submenu__chevron"
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <polyline points="9 6 15 12 9 18" />
      </svg>
    </button>

    <ul
      v-show="isOpen"
      class="itp-dropdown-submenu__flyout"
      role="menu"
      :style="flyoutStyle"
      @click="onFlyoutClick"
      @pointerenter="onFlyoutEnter"
      @pointerleave="onFlyoutLeave"
    >
      <slot />
    </ul>
  </li>
</template>

<style lang="scss">
.itp-dropdown-submenu {
  list-style: none;

  &__trigger {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    cursor: pointer;
  }

  &__label {
    flex: 1 1 auto;
    text-align: left;
  }

  &__chevron {
    flex-shrink: 0;
    opacity: 0.55;
    transition: opacity 0.12s ease;
  }

  &.is-active > &__trigger,
  & > &__trigger:hover {
    background-color: rgba(0, 0, 0, 0.04);

    .itp-dropdown-submenu__chevron {
      opacity: 0.9;
    }
  }

  &.is-pinned > &__trigger {
    background-color: rgba(0, 0, 0, 0.08);
    font-weight: 500;
  }
}

.itp-dropdown-submenu__flyout {
  .dropdown-item:hover,
  .dropdown-item:focus,
  .nav-link:hover,
  .nav-link:focus,
  .nav-item:hover,
  .itp-nav-item:hover {
    background-color: rgba(0, 0, 0, 0.06);
    color: var(--bs-body-color);
    text-decoration: none;
  }

  .dropdown-divider {
    height: 0;
    margin: 0.5rem 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }
}
</style>
